//
// Copyright (c) 2011-2022 by Curalate, Inc.
//

import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PojoSet } from 'pojo-sets';

import {
  EmailReportOutput,
  EmailReportTopic,
  EmailReportingSortDir,
  EmailReportingSortKey,
} from '@account-settings/redux/emailReports/emailReportsConstants';
import {
  IEmailReportsState,
  ISubscription,
} from '@account-settings/redux/emailReports/emailReportsModel';
import { displayTopic } from '@account-settings/redux/emailReports/emailReportsUtil';
import * as emailReportsApi from '@apis/account-settings/emailReportsApi';
import { getAllUsers } from '@apis/team-management/teamManagementApi';
import { ISettingsUser } from '@apis/team-management/teamManagementApiModel';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import ClientId from '@models/ClientId';
import { SliceNames } from '@redux/SliceNames';
import { createThunk } from '@redux/createThunk';

const initialState: IEmailReportsState = {
  subscriptions: [],
  clientUsers: [],
  reportTableSortDir: EmailReportingSortDir.ASC,
  reportTableSortKey: EmailReportingSortKey.RECIPIENT,
  createSubscriptionModal: {
    isOpen: false,
    isEditing: false,
    isAddSubmitted: false,
    users: PojoSet.empty<string>(),
    frequency: EmailReportTopic.INSIGHTS_WEEKLY,
    content: {
      curalateExperiences: false,
      facebookInsights: false,
      instagramInsights: false,
      tiktokInsights: false,
    },
    createdSubscriptions: undefined,
    updatedSubscriptions: undefined,
    untouchedSubscriptions: undefined,
    subscriptionBeingUpdated: undefined,
  },
  flags: {
    isRequestingSubscriptions: false,
    isAddingSubscriptions: false,
    isUpdatingSubscription: false,
    isDeletingSubscription: false,
  },
  errors: {
    requestSubscriptionsError: undefined,
    addSubscriptionsError: undefined,
    updateSubscriptionError: undefined,
    deleteSubscriptionError: undefined,
    getClientUsersError: undefined,
  },
};

const { actions, reducer } = createSlice({
  name: SliceNames.AccountSettingsEmailReports,
  initialState,
  reducers: {
    updateEmailReportingSort(
      state,
      action: PayloadAction<{
        newSortDir: EmailReportingSortDir;
        newSortKey: EmailReportingSortKey;
      }>,
    ) {
      state.reportTableSortDir = action.payload.newSortDir;
      state.reportTableSortKey = action.payload.newSortKey;
    },
    requestSubscriptions(state) {
      state.flags.isRequestingSubscriptions = true;
      state.errors.requestSubscriptionsError = undefined;
    },
    requestSubscriptionsSucceeded(
      state,
      action: PayloadAction<ISubscription[]>,
    ) {
      state.subscriptions = action.payload.sort((a, b) => {
        const primarySort = a.userEmail.localeCompare(b.userEmail);
        if (primarySort === 0) {
          return b.topic.localeCompare(a.topic);
        }
        return primarySort;
      });
      state.flags.isRequestingSubscriptions = false;
    },
    requestSubscriptionsFailed(state, action: PayloadAction<string>) {
      state.errors.requestSubscriptionsError = action.payload;
      state.flags.isRequestingSubscriptions = false;
    },
    requestAddSubscriptions(state) {
      state.flags.isAddingSubscriptions = true;
      state.errors.addSubscriptionsError = undefined;
    },
    requestAddSubscriptionsSucceeded(state) {
      state.flags.isAddingSubscriptions = false;
    },
    requestAddSubscriptionsFailed(state, action: PayloadAction<string>) {
      state.errors.addSubscriptionsError = action.payload;
      state.flags.isAddingSubscriptions = false;
    },
    requestUpdateSubscription(state) {
      state.flags.isUpdatingSubscription = true;
      state.errors.updateSubscriptionError = undefined;
    },
    requestUpdateSubscriptionSucceeded(state) {
      state.flags.isUpdatingSubscription = false;
    },
    requestUpdateSubscriptionFailed(state, action: PayloadAction<string>) {
      state.errors.updateSubscriptionError = action.payload;
      state.flags.isUpdatingSubscription = false;
    },
    requestDeleteSubscription(state) {
      state.flags.isDeletingSubscription = true;
      state.errors.deleteSubscriptionError = undefined;
    },
    requestDeleteSubscriptionSucceeded(state) {
      state.flags.isDeletingSubscription = false;
    },
    requestDeleteSubscriptionFailed(state, action: PayloadAction<string>) {
      state.errors.deleteSubscriptionError = action.payload;
      state.flags.isDeletingSubscription = false;
    },
    setSubscriptions(state, action: PayloadAction<ISubscription[]>) {
      state.subscriptions = action.payload;
    },
    setClientUsers(state, action: PayloadAction<ISettingsUser[]>) {
      state.clientUsers = action.payload;
    },
    getClientUsersFailed(state, action: PayloadAction<string>) {
      state.errors.getClientUsersError = action.payload;
    },
    openCreateSubscriptionModal(state) {
      state.createSubscriptionModal.isOpen = true;
      state.createSubscriptionModal.isEditing = false;
      state.createSubscriptionModal.isAddSubmitted = false;
      state.createSubscriptionModal.frequency =
        EmailReportTopic.INSIGHTS_WEEKLY;
      state.createSubscriptionModal.content = {
        curalateExperiences: false,
        facebookInsights: false,
        instagramInsights: false,
        tiktokInsights: false,
      };
    },
    openEditSubscriptionModal(state, action: PayloadAction<ISubscription>) {
      state.createSubscriptionModal.isOpen = true;
      state.createSubscriptionModal.isEditing = true;
      state.createSubscriptionModal.isAddSubmitted = false;
      state.createSubscriptionModal.users = PojoSet.from([
        action.payload.userEmail,
      ]);
      state.createSubscriptionModal.frequency = action.payload.topic;
      state.createSubscriptionModal.content = action.payload.config;
      state.createSubscriptionModal.subscriptionBeingUpdated = action.payload;
    },
    setIsAddSubmitted(state, action: PayloadAction<boolean>) {
      state.createSubscriptionModal.isAddSubmitted = action.payload;
    },
    closeSubscriptionModal(state) {
      state.createSubscriptionModal = initialState.createSubscriptionModal;
    },
    setSubscriptionModalFrequency(
      state,
      action: PayloadAction<EmailReportTopic>,
    ) {
      state.createSubscriptionModal.frequency = action.payload;
    },
    toggleSubscriptionModalContentExperiences(state) {
      state.createSubscriptionModal.content.curalateExperiences =
        !state.createSubscriptionModal.content.curalateExperiences;
    },
    toggleSubscriptionModalContentFacebook(state) {
      state.createSubscriptionModal.content.facebookInsights =
        !state.createSubscriptionModal.content.facebookInsights;
    },
    toggleSubscriptionModalContentInstagram(state) {
      state.createSubscriptionModal.content.instagramInsights =
        !state.createSubscriptionModal.content.instagramInsights;
    },
    toggleSubscriptionModalContentTiktok(state) {
      state.createSubscriptionModal.content.tiktokInsights =
        !state.createSubscriptionModal.content.tiktokInsights;
    },
    updateUsers(state, action: PayloadAction<PojoSet<string>>) {
      state.createSubscriptionModal.users = action.payload;
    },
    selectAllUsers(state, action: PayloadAction<string[]>) {
      state.createSubscriptionModal.users = PojoSet.from(action.payload);
    },
    deselectAllUsers(state) {
      state.createSubscriptionModal.users = PojoSet.empty();
    },
    setCreatedSubscriptions(state, action: PayloadAction<ISubscription[]>) {
      state.createSubscriptionModal.createdSubscriptions = action.payload;
    },
    setUpdatedSubscriptions(state, action: PayloadAction<ISubscription[]>) {
      state.createSubscriptionModal.updatedSubscriptions = action.payload;
    },
    setUntouchedSubscriptions(state, action: PayloadAction<ISubscription[]>) {
      state.createSubscriptionModal.untouchedSubscriptions = action.payload;
    },
  },
});

export const updateEmailReportingSort = createThunk(
  (newSortDir: EmailReportingSortDir, newSortKey: EmailReportingSortKey) =>
    (dispatch, getState) => {
      dispatch(actions.updateEmailReportingSort({ newSortDir, newSortKey }));
      const newSubscriptions = [
        ...getState().accountSettings.emailReports.subscriptions,
      ].sort((a, b) => {
        const sortMultiplier =
          newSortDir === EmailReportingSortDir.DESC ? -1 : 1;
        if (newSortKey === EmailReportingSortKey.RECIPIENT) {
          const primarySort =
            a.userEmail.localeCompare(b.userEmail) * sortMultiplier;
          if (primarySort === 0) {
            return b.topic.localeCompare(a.topic);
          }
          return primarySort;
        } else {
          return (
            ((a.lastSent?.getTime() ?? 0) - (b.lastSent?.getTime() ?? 0)) *
            sortMultiplier
          );
        }
      });
      dispatch(actions.setSubscriptions(newSubscriptions));
    },
);

export const getClientUsers = createThunk(() => async (dispatch) => {
  try {
    const users = await getAllUsers();
    dispatch(actions.setClientUsers(users as ISettingsUser[]));
  } catch (_e) {
    dispatch(
      actions.getClientUsersFailed(
        'An unexpected error occurred. Please try again or contact Bazaarvoice Support.',
      ),
    );
  }
});

export const getClientSubscriptions = createThunk(() => async (dispatch) => {
  dispatch(actions.requestSubscriptions());
  try {
    const clientSubscriptions = await emailReportsApi.getClientSubscriptions();
    dispatch(actions.requestSubscriptionsSucceeded(clientSubscriptions));
  } catch (_e) {
    const errorMessage =
      'An error occurred when trying to get your subscriptions.';
    dispatch(actions.requestSubscriptionsFailed(errorMessage));
    dispatch(
      toastNotificationsActions.addToastNotification({
        title: errorMessage,
        type: ToastType.ERROR,
        options: {
          toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
        },
      }),
    );
  }
});

export const updateSubscription = createThunk(
  () => async (dispatch, getState) => {
    dispatch(actions.requestUpdateSubscription());
    const { subscriptionBeingUpdated } =
      getState().accountSettings.emailReports.createSubscriptionModal;
    if (subscriptionBeingUpdated) {
      const subscription = {
        clientId: subscriptionBeingUpdated.clientId,
        userId: subscriptionBeingUpdated.userId,
        userEmail: subscriptionBeingUpdated.userEmail,
        config:
          getState().accountSettings.emailReports.createSubscriptionModal
            .content,
        output: EmailReportOutput.SENDGRID,
        topic:
          getState().accountSettings.emailReports.createSubscriptionModal
            .frequency,
        lastSent: subscriptionBeingUpdated.lastSent,
        version: subscriptionBeingUpdated.version,
      };
      try {
        await emailReportsApi.updateSubscription(subscription);
        dispatch(actions.requestUpdateSubscriptionSucceeded());
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Report Updated',
            message: `You've successfully updated ${subscription.userEmail}'s
          ${displayTopic(subscription.topic)} email report`,
            type: ToastType.SUCCESS,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );
        void dispatch(getClientSubscriptions());
      } catch (_e) {
        const errorMessage =
          'An error occurred when trying to update your subscription.';
        dispatch(actions.requestUpdateSubscriptionFailed(errorMessage));
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: errorMessage,
            type: ToastType.ERROR,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );
      }
    }
  },
);

export const deleteSubscription = createThunk(
  (subscription: ISubscription) => async (dispatch) => {
    dispatch(actions.requestDeleteSubscription());
    try {
      await emailReportsApi.deleteSubscription(subscription);
      dispatch(actions.requestDeleteSubscriptionSucceeded());
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Report Deleted',
          message: `You've successfully deleted ${subscription.userEmail}'s
        ${displayTopic(subscription.topic)} email report`,
          type: ToastType.SUCCESS,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
      void dispatch(getClientSubscriptions());
    } catch (_e) {
      const errorMessage =
        'An error occurred when trying to delete your subscription.';
      dispatch(actions.requestDeleteSubscriptionFailed(errorMessage));
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: errorMessage,
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    }
  },
);

export const toggleUser = createThunk(
  (username: string) => (dispatch, getState) => {
    const { users } =
      getState().accountSettings.emailReports.createSubscriptionModal;
    dispatch(
      actions.updateUsers(
        PojoSet.toggle(users, username, !PojoSet.has(users, username)),
      ),
    );
  },
);

export const addSubscriptions = createThunk(
  () => async (dispatch, getState) => {
    const clientId = getState().session.client.id;
    const { clientUsers } = getState().accountSettings.emailReports;
    dispatch(actions.requestAddSubscriptions());
    const users = PojoSet.toArray(
      getState().accountSettings.emailReports.createSubscriptionModal.users,
    );
    const created: ISubscription[] = [];
    const updated: ISubscription[] = [];
    const untouched: ISubscription[] = [];
    let newSubscriptions: ISubscription[] = [];

    users.map((userEmail) => {
      const userId = clientUsers.find((user) => user.email === userEmail)?.id;

      if (userId) {
        const mightUpdate =
          getState().accountSettings.emailReports.subscriptions.find(
            (subscription) =>
              userEmail === subscription.userEmail &&
              subscription.topic ===
                getState().accountSettings.emailReports.createSubscriptionModal
                  .frequency,
          );

        if (mightUpdate) {
          const newContent =
            getState().accountSettings.emailReports.createSubscriptionModal
              .content;

          if (
            (newContent.curalateExperiences &&
              !mightUpdate.config.curalateExperiences) ||
            (newContent.facebookInsights &&
              !mightUpdate.config.facebookInsights) ||
            (newContent.instagramInsights &&
              !mightUpdate.config.instagramInsights) ||
            (newContent.tiktokInsights && !mightUpdate.config.tiktokInsights)
          ) {
            const updatedSubscription = {
              clientId: ClientId.fromString(clientId),
              userId,
              userEmail: mightUpdate.userEmail,
              topic: mightUpdate.topic,
              output: EmailReportOutput.SENDGRID,
              config: {
                curalateExperiences:
                  mightUpdate.config.curalateExperiences ||
                  newContent.curalateExperiences,
                facebookInsights:
                  mightUpdate.config.facebookInsights ||
                  newContent.facebookInsights,
                instagramInsights:
                  mightUpdate.config.instagramInsights ||
                  newContent.instagramInsights,
                tiktokInsights:
                  mightUpdate.config.tiktokInsights ||
                  newContent.tiktokInsights,
              },
              lastSent: mightUpdate.lastSent,
              version: mightUpdate.version,
            };
            updated.push(updatedSubscription);
            newSubscriptions.push(updatedSubscription);
          } else {
            untouched.push(mightUpdate);
          }
        } else {
          const newSubscription = {
            clientId: ClientId.fromString(clientId),
            userId,
            userEmail,
            topic:
              getState().accountSettings.emailReports.createSubscriptionModal
                .frequency,
            output: EmailReportOutput.SENDGRID,
            config:
              getState().accountSettings.emailReports.createSubscriptionModal
                .content,
            lastSent: undefined,
            version: 0,
          };
          created.push(newSubscription);
          newSubscriptions.push(newSubscription);
        }
      }
    });

    try {
      await emailReportsApi.addSubscriptions([...updated, ...created]);

      dispatch(actions.requestAddSubscriptionsSucceeded());

      newSubscriptions = [
        ...getState().accountSettings.emailReports.subscriptions.slice(),
        ...newSubscriptions,
      ];
      dispatch(actions.setCreatedSubscriptions(created));
      dispatch(actions.setUpdatedSubscriptions(updated));
      dispatch(actions.setUntouchedSubscriptions(untouched));
      dispatch(actions.setIsAddSubmitted(true));
    } catch (_e) {
      const errorMessage =
        'An error occurred when trying to create your subscriptions.';
      dispatch(actions.requestAddSubscriptionsFailed(errorMessage));
      dispatch(
        toastNotificationsActions.addToastNotification({
          title: errorMessage,
          type: ToastType.ERROR,
          options: {
            toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
          },
        }),
      );
    }
  },
);

export const {
  closeSubscriptionModal,
  deselectAllUsers,
  openCreateSubscriptionModal,
  openEditSubscriptionModal,
  selectAllUsers,
  setIsAddSubmitted,
  setSubscriptionModalFrequency,
  toggleSubscriptionModalContentExperiences,
  toggleSubscriptionModalContentFacebook,
  toggleSubscriptionModalContentInstagram,
  toggleSubscriptionModalContentTiktok,
} = actions;

export default reducer;
